import { useCallback, useContext, useMemo } from 'react';
import { SettingsServiceContext } from 'common/settingsService/impl/provider/SettingsServiceContext';
import { SettingsServiceTypes as Types } from 'common/settingsService/types';
import { logger } from 'src/utils/logger';
import { TimesheetSettingsKeyModel } from 'src/graphql/types';

interface UseSettingsServiceOutput {
  isEnabled<GROUP_KEY extends Types.GroupNames>(
    groupKey: GROUP_KEY,
    itemKey: Types.GroupItemKeysFor<GROUP_KEY>
  ): boolean;
  broadcast<GROUP_KEY extends Types.GroupNames>(groupKey: GROUP_KEY, items: Types.GroupItemsModelFor<GROUP_KEY>): void;
  loading: boolean;
}

export function useSettingsService(): UseSettingsServiceOutput {
  const { state, broadcast: emit, loading } = useContext(SettingsServiceContext);
  const broadcast: UseSettingsServiceOutput['broadcast'] = useCallback(
    (groupKey, items) => {
      if (items) {
        emit(groupKey, items);
      }
    },
    [emit]
  );

  const isEnabled: UseSettingsServiceOutput['isEnabled'] = useCallback(
    (groupKey, itemKey): boolean => {
      // expand a type to catch
      const assertResult = (
        state[groupKey]?.items?.[itemKey.toString() as TimesheetSettingsKeyModel] as Types.ItemBodyModel
      )?.enabled;
      if (typeof assertResult !== 'boolean') {
        if (process.env.DEBUG === 'true') {
          logger.info(
            `Setting Item "${itemKey.toString()}" in group ${groupKey} is not defined or null. This is just a notification not a bug.`
          );
        }
        return false;
      }
      return assertResult;
    },
    [state]
  );
  return useMemo(() => ({ isEnabled, broadcast, loading }), [isEnabled, broadcast, loading]);
}
